import React, { useState } from "react"

import { Field, Form, Formik } from "formik"
import { NotificationManager } from "react-notifications"
import * as Yup from "yup"

import {
  Button,
  FormControl,
  FormErrorMessage,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useTheme,
} from "@chakra-ui/core"
import { PlusIcon } from "@heroicons/react/24/outline"

import { gql, useClient, useMutation } from "hooks/urql"

import {
  AutocompleteField,
  FormFailures,
  handleFailure,
} from "components/Forms/Formik/hookComponents"

const ValidationSchema = Yup.object().shape({
  userId: Yup.string().required("Please select a tutor"),
})

export type AddTutorProps = {
  messageThreadId: string
}

const AddTutor: React.FC<AddTutorProps> = ({ messageThreadId }) => {
  const theme: any = useTheme()
  const [modalOpen, setModalOpen] = useState(false)

  const client = useClient()
  const [, runMutation]: any[] = useMutation(addUserMutation, client)
  const formState = {
    messageThreadId,
    userId: "",
  }

  const handleSubmit = (values, actions) => {
    runMutation(values)
      .then(
        result => {
          const { failures } = result.data.messageThreadAddUser
          if (failures.length > 0) {
            handleFailure(actions, failures)
          } else {
            NotificationManager.info("Tutor added")
            setModalOpen(false)
          }
        },
        () => handleFailure(actions)
      )
      .catch(() => handleFailure(actions))
  }

  return (
    <>
      <div className="flex flex-col items-center space-y-1">
        <button
          className="m-0 flex h-16 w-16 cursor-pointer items-center justify-center rounded-full border-0 bg-gray-300 p-0"
          onClick={() => setModalOpen(true)}
        >
          <PlusIcon className="m-0 h-12 w-12 p-0 text-white" />
        </button>
        <div>Add Tutor</div>
      </div>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)}>
        <ModalOverlay />
        <ModalContent>
          <Formik
            initialValues={formState}
            validationSchema={ValidationSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting }) => (
              <Form>
                <ModalHeader>Add Tutor to Thread</ModalHeader>
                <ModalBody>
                  <Field name="recipientId">
                    {({ field, form }) => (
                      <FormControl isInvalid={!!form.errors[field.name]} mt="4">
                        <AutocompleteField
                          api="/api/teachers/message_threads/autocomplete_messageable_users"
                          name="userId"
                          label="Tutor Name"
                          hideError
                        />
                        <FormErrorMessage>
                          {form.errors[field.name]}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </ModalBody>

                <ModalFooter>
                  <div className="flex w-full flex-col space-y-4">
                    <FormFailures />
                    <div className="flex w-full justify-between">
                      <Button
                        {...theme.buttonVariants.view}
                        onClick={() => setModalOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        {...theme.buttonVariants.cloudroom}
                        disabled={isSubmitting}
                      >
                        Add Tutor
                      </Button>
                    </div>
                  </div>
                </ModalFooter>
              </Form>
            )}
          </Formik>
        </ModalContent>
      </Modal>
    </>
  )
}

const addUserMutation = gql`
  mutation AddUser($messageThreadId: ID!, $userId: ID!) {
    messageThreadAddUser(messageThreadId: $messageThreadId, userId: $userId) {
      failures {
        message
      }
      messageThread {
        id
      }
    }
  }
`

export default AddTutor
